import { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import * as api from '../../../services/api';

const OTP_LENGTH = 4;

const OtpStep = ({ mobile, onBack }) => {
  const { setAuth } = useAuth();
  const navigate = useNavigate();
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResendMessage('');
    const digits = otp.replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (digits.length !== OTP_LENGTH) {
      setError(`Enter the ${OTP_LENGTH}-digit OTP`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.verifyOtp(mobile, digits);
      if (res.success && res.data?.token) {
        const admin = res.data.admin ?? null;
        setAuth(res.data.token, admin);
        navigate('/dashboard', { replace: true });
        return;
      }
      setError(res.message || 'Verification failed.');
    } catch (err) {
      const res = err.response?.data;
      const code = res?.code;
      const msg = res?.message || err.message || 'Verification failed.';
      if (code === 'OTP_EXPIRED') {
        setError('OTP has expired. Please request a new one.');
      } else if (code === 'OTP_MAX_ATTEMPTS') {
        setError('Too many attempts. Please request a new OTP.');
      } else if (code === 'OTP_INVALID' || code === 'OTP_NOT_FOUND') {
        setError(msg);
      } else {
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    setError('');
    setResendMessage('');
    setResending(true);
    try {
      const res = await api.requestLogin(mobile);
      if (res.success && (res.data?.otp_sent || res.data)) {
        setResendMessage('OTP sent again to your mobile.');
        setOtp('');
      } else {
        setError(res.message || 'Failed to resend OTP.');
      }
    } catch (err) {
      const res = err.response?.data;
      setError(res?.message || err.message || 'Failed to resend OTP.');
    } finally {
      setResending(false);
    }
  };

  return (
    <form className="authentication-form" onSubmit={handleSubmit}>
      <p className="text-muted small mb-3">OTP sent to {mobile}</p>
      <Form.Group className="mb-3">
        <Form.Control
          id="login-otp"
          type="text"
          inputMode="numeric"
          maxLength={OTP_LENGTH + 2}
          placeholder="Enter OTP"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH))}
          isInvalid={!!error}
          disabled={submitting}
          autoComplete="one-time-code"
        />
        {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
        {resendMessage && <Form.Text className="text-success">{resendMessage}</Form.Text>}
      </Form.Group>
      <div className="mb-1 text-center d-grid">
        <Button variant="primary" type="submit" disabled={submitting}>
          {submitting ? 'Verifying…' : 'Verify & sign in'}
        </Button>
      </div>
      <div className="text-center mt-2 d-flex flex-column gap-1">
        <Button
          variant="link"
          type="button"
          className="p-0 text-muted small"
          onClick={handleResendOtp}
          disabled={resending || submitting}
        >
          {resending ? 'Sending…' : 'Resend OTP'}
        </Button>
        <Button variant="link" type="button" className="p-0 text-muted small" onClick={onBack}>
          Use another number
        </Button>
      </div>
    </form>
  );
};

export default OtpStep;
